import useComponentSize from '@rehooks/component-size';
import { ComponentType, ReactNode, SVGProps, useRef } from 'react';

import { IconX } from '../../common/icons/cdl/X';
import { Box } from '../../common/ui/Box';
import { IconButton } from '../IconButton/IconButton';

import { CalloutContent } from './CalloutContent';
import { CalloutWrapper } from './CalloutWrapper';
import { getVariantColors } from './getVariantColors';

export type CalloutVariant = 'default' | 'accent' | 'attention' | 'negative' | 'positive' | 'special';

interface SharedCalloutProps {
    Icon?: ComponentType<SVGProps<SVGSVGElement>> | null;
    variant?: CalloutVariant;
    /**
     * Can only be used when no children are provided
     */
    onDismiss?: () => void;
    children?: ReactNode;
}

type EnsureHeadingOrDescription =
    | {
          heading: ReactNode;
          description?: never;
      }
    | {
          heading?: never;
          description: ReactNode;
      }
    | {
          heading: ReactNode;
          description: ReactNode;
      };

export type CalloutProps = SharedCalloutProps & EnsureHeadingOrDescription;

export const Callout = ({ variant = 'default', Icon, ...props }: CalloutProps) => {
    const calloutRef = useRef<HTMLDivElement>(null);
    const { width } = useComponentSize(calloutRef);

    const variantColors = getVariantColors(variant);

    // Design defined breakpoint for the layout of the callout
    const isWide = width > 600;

    if (!props.heading && !props.children && props.description) {
        return (
            <CalloutWrapper
                ref={calloutRef}
                backgroundColor={variantColors.background}
                borderColor={variantColors.border}
                borderWidth="0px"
                borderRadius={4}
                isWide={isWide}
            >
                <CalloutContent description={props.description} Icon={Icon} color={variantColors.color} />

                {props.onDismiss && !props.children ? (
                    <Box position="absolute" top={8} right={8}>
                        <IconButton Icon={IconX} onClick={props.onDismiss} />
                    </Box>
                ) : null}
            </CalloutWrapper>
        );
    }

    return (
        <CalloutWrapper
            ref={calloutRef}
            backgroundColor={variantColors.background}
            borderColor={variantColors.border}
            borderWidth="1px"
            borderRadius={8}
            isWide={isWide}
        >
            <CalloutContent
                heading={props.heading}
                description={props.description}
                Icon={Icon}
                color={variantColors.color}
            />

            {props.children}

            {props.onDismiss && !props.children ? (
                <Box position="absolute" top={8} right={8}>
                    <IconButton Icon={IconX} onClick={props.onDismiss} />
                </Box>
            ) : null}
        </CalloutWrapper>
    );
};
